// @ts-nocheck
import { useRef, useState } from 'react'
import Slider from 'react-slick'
import clsx from 'clsx'
import { useSession } from 'next-auth/react'

import { SeoEvent } from 'lib/gtm'
import { LoginModal } from 'modules/auth'
import {
  CustomerTypes,
  DateOfBirthModal,
  extractCustomerFromClientSession
} from 'modules/customer'
import { getPromotionIdFromUrl } from 'shared/analytics'
import IconButton, {
  type IconButtonProps
} from 'shared/ui/IconButton/IconButton'
import LazyLoader from 'shared/ui/LazyLoader/LazyLoader'
import toast from 'shared/ui/utils/toast'
import { noop } from 'shared/utils/common'
import { isNilOrEmpty, isNotNilOrEmpty } from 'shared/utils/ramda'

import { sliderPromotion } from '../../../analytics'
import { BannerTypes, trackBannerClick } from '../../../submodules/banners/api'

import BannerSlide from './BannerSlide'

import 'slick-carousel/slick/slick.css'
import 'slick-carousel/slick/slick-theme.css'
import styles from './BannerSlider.module.scss'

const settings = {
  dots: false,
  infinite: true,
  speed: 500,
  slidesToShow: 1,
  slidesToScroll: 1,
  autoplay: true,
  autoplaySpeed: 5000,
  arrows: true,
  swipe: true,
  adaptiveHeight: false,
  // accessibility: true,
  pauseOnHover: true,
  pauseOnFocus: true,
  dots: false,
  infinite: true
}

interface SliderProps extends React.HTMLAttributes<HTMLDivElement> {
  banners: BannerTypes[]
  creativeSlotSeoPrefix: string
}

function BannerSlider({
  banners,
  creativeSlotSeoPrefix,
  ...props
}: SliderProps) {
  const session = useSession()

  const sliderRef = useRef<Slider | null>(null)

  const user: CustomerTypes | undefined = extractCustomerFromClientSession(session)
  const isLoggedIn = isNotNilOrEmpty(user)

  const [loginModalOpen, setLoginModalOpen] = useState(false)
  const [dobModalOpen, setDobModalOpen] = useState(false)
  const [viewedBanners, setViewedBanners] = useState(new Set([]))
  const [currentSlide, setCurrentSlide] = useState(0)

  const hasMultipleBanners = (banners?.length || 0) > 1

  const sliderSettings = {
    ...settings,
    // Disable features that cause slide cloning when there is only one banner
    infinite: hasMultipleBanners,
    autoplay: hasMultipleBanners,
    arrows: hasMultipleBanners,
    swipe: hasMultipleBanners,
    dots: hasMultipleBanners,
    afterChange: (current: number) => setCurrentSlide(current),
    prevArrow: (
      <StyledNextPrevButton
        variant='transparent'
        size='small'
        onClick={() => sliderRef.current?.slickPrev()}
        aria-label='Poprzedni baner'
      ></StyledNextPrevButton>
    ),
    nextArrow: (
      <StyledNextPrevButton
        variant='transparent'
        size='small'
        aria-label='Następny baner'
      ></StyledNextPrevButton>
    )
  }

  const handleCountingBannerClicks = (bannerId: string) => {
    void trackBannerClick(bannerId).finally(() => noop)
  }

  const handleCloseDobModal = async () => {
    if (window) {
      window.location?.reload()
    }
    setTimeout(() => {
      setDobModalOpen(false)
    }, 1000)
  }

  const handleCloseLoginModal = () => {
    setLoginModalOpen(false)
  }

  const handlePromoClick = (banner: BannerTypes, idx: number) => {
    banner.id && handleCountingBannerClicks(banner.id)
    sliderPromotion({
      event: SeoEvent.SELECT_PROMOTION,
      customer: user,
      ecommerce: {
        creative_name: banner.name,
        creative_slot: `${creativeSlotSeoPrefix}_${idx + 1}`,
        promotion_id: getPromotionIdFromUrl(banner?.destinationUrl),
        promotion_name: undefined
      }
    })
  }

  const handleViewPromotion = (banner: BannerTypes, idx: number) => {
    setViewedBanners(prev => new Set([...prev, banner.id]))

    sliderPromotion({
      bannerId: banner?.id || undefined,
      event: SeoEvent.VIEW_PROMOTION,
      customer: user,
      ecommerce: {
        creative_name: banner?.name || undefined,
        creative_slot: `${creativeSlotSeoPrefix}_${idx + 1}`,
        promotion_id: getPromotionIdFromUrl(banner?.destinationUrl),
        promotion_name: undefined
      }
    })
  }

  const handleBlurredBannerClick = () => {
    if (!isLoggedIn) {
      setLoginModalOpen(true)

      return
    }

    if (isLoggedIn && isNilOrEmpty(user?.dateOfBirth)) {
      setDobModalOpen(true)

      return
    }

    toast.error('Promocja z ograniczeniem wiekowym')
  }

  const handleStopAutoplay = () => {
    sliderRef.current?.slickPause()
  }

  const handleStartAutoplay = () => {
    sliderRef.current?.slickPlay()
  }

  return (
    <Wrapper onFocus={handleStopAutoplay} onBlur={handleStartAutoplay}>
      <LoginModal open={loginModalOpen} onClose={handleCloseLoginModal} />
      <DateOfBirthModal open={dobModalOpen} onClose={handleCloseDobModal} />

      <Slider ref={sliderRef} {...sliderSettings} {...props}>
        {isNotNilOrEmpty(banners) ? (
          banners.map((banner, idx) => {
            const isBlurred = !banner.ageLimitMet
            const isActive = idx === currentSlide

            const isConfirmAgeRequired = isNotNilOrEmpty(user?.dateOfBirth)
            const Loader = idx < 1 ? 'div' : LazyLoader
            const isViewed = viewedBanners.has(banner.id)

            return (
              <Loader key={banner.id}>
                <BannerSlide
                  className={styles.slide}
                  handleViewPromotion={handleViewPromotion}
                  banner={banner}
                  isBlurred={isBlurred}
                  idx={idx}
                  user={user}
                  handleBlurredBannerClick={handleBlurredBannerClick}
                  handlePromoClick={handlePromoClick}
                  alt={banner.title || banner.name}
                  isConfirmAgeRequired={isConfirmAgeRequired}
                  isViewed={isViewed}
                  isActive={isActive}
                />
              </Loader>
            )
          })
        ) : (
          <div className={styles.placeholder} />
        )}
      </Slider>
    </Wrapper>
  )
}

export default BannerSlider

const Wrapper = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div {...props} className={clsx(styles.wrapper, className)} />
)

const StyledNextPrevButton = ({
  className,
  size = 'medium',
  variant = 'green',
  disabled = false,
  ...props
}: IconButtonProps) => (
  <IconButton
    {...props}
    size={size}
    variant={variant}
    disabled={disabled}
    className={clsx(styles.nextPrevButton, className)}
  />
)
